import { Switch } from '@headlessui/react';
import { PencilSquareIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import {
  PlusCircleIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  ClockIcon,
  ShoppingBagIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { Radio, DatePicker } from 'antd';
import React, { useState, useEffect } from 'react';
import { TWButton, TWModal, TWDataTable } from '../../../../components/ui';
import { headerCls, EditAction } from '../../../../data/columns';
import SubscriptionUpsertModal from '../../../../components/subscription/subscription-upsert-modal';
import ExtraFeesUpsertModal from './components/ExtraFeesUpsertModal';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import companyApi from '../../../../services/companyApi';
import storeApi from '../../../../services/storeApi';
import dayjs from 'dayjs';
import CurrencyInput from 'react-currency-input-field';
import { OrderTypes, PlatformTypes } from '../../../../data/enums';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FieldLabel = ({ children }) => (
  <label className="block text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
    {children}
  </label>
);

const SectionCard = ({
  gradient,
  icon: Icon,
  title,
  description,
  action,
  children,
}) => (
  <section className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white shadow-sm`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {description}
            </p>
          )}
        </div>
      </div>
      {action}
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

const inputClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400/20';

const tagClass = (idx) => {
  const colors = [
    'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200',
    'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300',
    'bg-blue-100 text-blue-700 dark:bg-sky-500/10 dark:text-sky-300',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  ];
  return colors[idx % colors.length];
};

const orderTypeTag = (v) => {
  if (v === 0) return tagClass(0);
  if (v === 10) return tagClass(1);
  if (v === 20) return tagClass(2);
  return tagClass(3);
};

const Fees = ({ companyDetail, updateCompany, setNotf }) => {
  const [storeList, setStoreList] = useState([]);
  const [form, setForm] = useState({
    creditcardfeeper: companyDetail.creditcardfeeper,
    creditcardfeerate: companyDetail.creditcardfeerate,
    itemfee: companyDetail.itemfee,
  });
  const [allExtraFees, setAllExtraFees] = useState([]);
  const [selectedExtraFee, setSelectedExtraFee] = useState({});
  const [selectedStore, setSelectedStore] = useState({});
  const [loading, setLoading] = useState({
    listLoading: false,
    upsertLoading: false,
    deleteLoading: false,
    creditCardLoading: false,
    storeListLoading: false,
    serviceCharge: false,
    customerFeeLoading: false,
    itemFee: false,
    extraFeesLoading: false,
  });
  const [dialogs, setDialogs] = useState({
    subscriptionUpsert: false,
    creditCardUpsert: false,
    delete: false,
    customerFeeUpsert: false,
    extraFeesUpsert: false,
  });

  const getStoreList = async () => {
    try {
      setLoading((prev) => ({ ...prev, storeListLoading: true }));
      const query = `companyid=${companyDetail.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
        const extraFees = list.data.data
          .map((store) =>
            store.extrafees.map((fee) => ({
              ...fee,
              storename: store.name,
            })),
          )
          .flat();
        setAllExtraFees(extraFees);
      }
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      setLoading((prev) => ({ ...prev, storeListLoading: false }));
    }
  };

  const onClickEdit = () => {
    setDialogs((prev) => ({ ...prev, subscriptionUpsert: true }));
  };

  const onCloseUpsert = () => {
    setDialogs((prev) => ({ ...prev, subscriptionUpsert: false }));
  };

  const onCloseUpsertExtraFee = () => {
    setSelectedExtraFee({});
    setDialogs((prev) => ({ ...prev, extraFeesUpsert: false }));
  };

  const onCloseUpsertCreditCard = () => {
    setDialogs((prev) => ({ ...prev, creditCardUpsert: false }));
  };

  const onClickEditCreditCardFee = () => {
    setDialogs((prev) => ({ ...prev, creditCardUpsert: true }));
  };

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onFormSubmit = async (trialend) => {
    const body = {
      ...companyDetail,
      ...form,
      ...(trialend && { trialend }),
    };
    const formData = new FormData();
    Object.keys(body).forEach((key) => {
      if (body[key]) formData.append(key, body[key]);
    });
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    try {
      setLoading((prev) => ({ ...prev, creditCardLoading: true }));
      await companyApi.upsert(formData, config);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      updateCompany();
      setLoading((prev) => ({ ...prev, creditCardLoading: false }));
      setDialogs((prev) => ({ ...prev, creditCardUpsert: false }));
    }
  };

  const onSubmitExtraFee = async (data) => {
    try {
      const { id, storeid, price, israte, ordertypes, platforms, name } = data;
      const oldStore = storeList.find((store) =>
        store.extrafees.some((fee) => fee.id === id),
      );
      const oldStoreId = oldStore?.id;
      const existExtraFees =
        storeList.find((store) => store.id === storeid)?.extrafees || [];
      const newFee = {
        ...(id && { id }),
        name,
        price,
        israte: Boolean(israte),
        ordertypes,
        platforms,
      };
      if (oldStoreId && oldStoreId !== storeid) {
        const updatedOldFees = oldStore.extrafees.filter(
          (fee) => fee.id !== id,
        );
        await storeApi.upsertExtraFee({
          storeid: oldStoreId,
          fees: updatedOldFees,
        });
      }
      const body = {
        storeid,
        fees: [...existExtraFees.filter((fee) => fee.id !== id), newFee],
      };
      setLoading((prev) => ({ ...prev, extraFeesLoading: true }));
      await storeApi.upsertExtraFee(body);
      updateCompany();
      setDialogs((prev) => ({ ...prev, extraFeesUpsert: false }));
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error.message || 'Something went wrong',
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, extraFeesLoading: false }));
    }
  };

  const onChangeServiceCharge = async (val, key, index) => {
    try {
      setLoading((prev) => ({ ...prev, serviceCharge: true }));
      const body = {
        ...storeList[index],
        [key]: val,
        websiteclosedat: storeList[index].websiteclosedat || '',
        apartment: storeList[index].apartment || '',
      };
      const formData = new FormData();
      Object.keys(body).forEach((k) => {
        if (body[k]) formData.append(k, body[k]);
      });
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await storeApi.upsert(formData, config);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      getStoreList();
      setLoading((prev) => ({ ...prev, serviceCharge: false }));
    }
  };

  const checkUpdate = (val) => {
    if (val) {
      setNotf((prev) => ({
        ...prev,
        message: 'Store Updated Successfully',
        display: true,
        type: 'success',
      }));
    } else {
      setNotf((prev) => ({
        ...prev,
        message: 'Store Update Failed',
        display: true,
        type: 'error',
      }));
    }
  };

  const onChangeItemFee = async (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onBlurSaveItemFee = async (val, key) => {
    val = parseFloat(val.replace('$', ''));
    try {
      setLoading((prev) => ({ ...prev, itemFee: true }));
      const body = { ...companyDetail, [key]: val };
      const formData = new FormData();
      Object.keys(body).forEach((k) => {
        if (body[k]) formData.append(k, body[k]);
      });
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await companyApi.upsert(formData, config);
    } catch (error) {
      setNotf((prev) => ({ ...prev, message: error, display: true }));
    } finally {
      updateCompany();
      setLoading((prev) => ({ ...prev, itemFee: false }));
    }
  };

  const onClickOpenCustomerFeeUpsertModal = (store) => {
    setSelectedStore(store);
    setDialogs((prev) => ({ ...prev, customerFeeUpsert: true }));
  };

  const onCloseUpsertCustomerFee = () => {
    setDialogs((prev) => ({ ...prev, customerFeeUpsert: false }));
  };

  const onFormCustomerFeeChange = (val, index, key) => {
    setSelectedStore((prev) => ({
      ...prev,
      prices: prev.prices.map((price, i) =>
        i === index ? { ...price, [key]: val } : price,
      ),
    }));
  };

  const onSubmitCustomerFee = async () => {
    try {
      setLoading((prev) => ({ ...prev, customerFeeLoading: true }));
      const { id, prices } = selectedStore;
      const body = { storeid: id, prices };
      await storeApi.upsertTierPrice(body);
      const formData = new FormData();
      for (const key of Object.keys(selectedStore)) {
        if (key === 'websiteclosedat') {
          formData.append(key, selectedStore[key] || '');
        } else {
          formData.append(key, selectedStore[key]);
        }
      }
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await storeApi.upsert(formData, config);
      checkUpdate(true);
    } catch (error) {
      checkUpdate(false);
    } finally {
      getStoreList();
      setLoading((prev) => ({ ...prev, customerFeeLoading: false }));
      setDialogs((prev) => ({ ...prev, customerFeeUpsert: false }));
    }
  };

  const onClickAddCustomerFee = () => {
    setSelectedStore((prev) => ({
      ...prev,
      prices: [...prev.prices, { startprice: 0, endprice: 0, fee: 0 }],
    }));
  };

  const onClickDeletePrice = (index) => {
    setSelectedStore((prev) => ({
      ...prev,
      prices: prev.prices.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    getStoreList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickOpenExtraFeeUpsert = () => {
    setSelectedExtraFee({});
    setDialogs((prev) => ({ ...prev, extraFeesUpsert: true }));
  };

  const onClickEditExtraFee = (fee) => {
    setSelectedExtraFee(fee);
    setDialogs((prev) => ({ ...prev, extraFeesUpsert: true }));
  };

  const onClickDeleteExtraFee = async (selected) => {
    const { id, storeid } = selected;
    try {
      const existExtraFees =
        storeList.find((store) => store.id === storeid)?.extrafees || [];
      const updatedFees = existExtraFees.filter((fee) => fee.id !== id);
      const body = { storeid, fees: updatedFees };
      setLoading((prev) => ({ ...prev, extraFeesLoading: true }));
      await storeApi.upsertExtraFee(body);
      updateCompany();
      setDialogs((prev) => ({ ...prev, extraFeesUpsert: false }));
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error.message || 'Something went wrong',
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, extraFeesLoading: false }));
    }
  };

  const isLoading = loading.storeListLoading;

  if (isLoading) {
    return (
      <div className="space-y-5">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 !rounded-md" />
              <SkeletonBlock className="h-5 w-40" />
            </div>
            <div className="mt-4 space-y-3">
              <SkeletonBlock className="h-10 w-full" />
              <SkeletonBlock className="h-10 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Credit Card Fee */}
      <SectionCard
        gradient="from-blue-500 to-indigo-600"
        icon={CreditCardIcon}
        title="Credit Card Fee"
        description="Processing fees applied to each card transaction."
        action={
          <button
            type="button"
            onClick={onClickEditCreditCardFee}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/60"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Edit
          </button>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Percentage
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {(companyDetail.creditcardfeerate || 0).toFixed(2)}
              <span className="ml-0.5 text-base font-semibold text-slate-500">
                %
              </span>
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Per Transaction
            </p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
              <span className="mr-0.5 text-base font-semibold text-slate-500">
                $
              </span>
              {(companyDetail.creditcardfeeper || 0).toFixed(2)}
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Extra Fees */}
      <SectionCard
        gradient="from-purple-500 to-pink-600"
        icon={Squares2X2Icon}
        title="Extra Fees"
        description="Custom fees applied per store, order type, or platform."
        action={
          <TWButton
            variant="primary"
            size="sm"
            icon={PlusCircleIcon}
            label="Add Fee"
            onClick={onClickOpenExtraFeeUpsert}
          />
        }
      >
        {allExtraFees.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 py-10 text-center dark:border-slate-600">
            <Squares2X2Icon className="h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No extra fees yet.
            </p>
          </div>
        ) : (
          <TWDataTable
            columns={[
              {
                title: <span className={headerCls}>Name</span>,
                key: 'name',
                render: (_, fee) => (
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {fee.name}
                  </span>
                ),
              },
              {
                title: <span className={headerCls}>Store</span>,
                key: 'storename',
                render: (_, fee) => (
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {fee.storename}
                  </span>
                ),
              },
              {
                title: <span className={headerCls}>Order Types</span>,
                key: 'ordertypes',
                render: (_, fee) => (
                  <div className="flex flex-wrap gap-1">
                    {fee.ordertypes?.map((ordertype, i) => (
                      <span
                        key={`${fee.id}-ot-${i}`}
                        className={classNames(
                          orderTypeTag(ordertype),
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        )}
                      >
                        {OrderTypes[ordertype]}
                      </span>
                    ))}
                  </div>
                ),
              },
              {
                title: <span className={headerCls}>Platforms</span>,
                key: 'platforms',
                render: (_, fee) => (
                  <div className="flex flex-wrap gap-1">
                    {fee.platforms?.map((platform, i) => (
                      <span
                        key={`${fee.id}-pl-${i}`}
                        className={classNames(
                          orderTypeTag(platform),
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        )}
                      >
                        {PlatformTypes[platform]}
                      </span>
                    ))}
                  </div>
                ),
              },
              {
                title: (
                  <span className={`${headerCls} block text-right`}>Price</span>
                ),
                key: 'price',
                align: 'right',
                render: (_, fee) =>
                  fee.israte ? (
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {Number(fee.price || 0)}%
                      <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                        of subtotal
                      </span>
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      ${Number(fee.price || 0).toFixed(2)}
                    </span>
                  ),
              },
              {
                title: <span className="sr-only">Actions</span>,
                key: 'actions',
                align: 'right',
                width: 60,
                render: (_, fee) => (
                  <EditAction onClick={() => onClickEditExtraFee(fee)} />
                ),
              },
            ]}
            data={allExtraFees}
            rowKey="id"
          />
        )}
      </SectionCard>

      {/* Subscription */}
      <SectionCard
        gradient="from-emerald-500 to-teal-600"
        icon={RocketLaunchIcon}
        title="Subscription"
        description="Plan fees and per-order charges."
        action={
          <button
            type="button"
            onClick={onClickEdit}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/60"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Edit
          </button>
        }
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Plan
            </p>
            <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {companyDetail.subscription?.name || '—'}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Order Fee
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
              ${(companyDetail.subscription?.orderfee || 0).toFixed(2)}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Monthly Fee
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
              ${(companyDetail.subscription?.monthlyfee || 0).toFixed(2)}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400">
              Order Fee Rate
            </p>
            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
              {(companyDetail.subscription?.orderfeerate || 0).toFixed(2)}%
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Trial End */}
      {!companyDetail.subscriptionid && (
        <SectionCard
          gradient="from-amber-500 to-orange-600"
          icon={ClockIcon}
          title="Trial End"
          description="Date when the free trial ends for this company."
        >
          <DatePicker
            value={
              companyDetail.trialend
                ? dayjs(companyDetail.trialend, 'YYYY-MM-DD hh:mm')
                : null
            }
            format={'MM/DD/YYYY'}
            onChange={(e) => {
              onFormSubmit(dayjs(e).endOf('day').format('YYYY-MM-DD hh:mm'));
            }}
            style={{ width: '100%' }}
            size="large"
            placeholder="Select trial end date."
          />
        </SectionCard>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Service Charge */}
        <SectionCard
          gradient="from-rose-500 to-red-600"
          icon={ReceiptPercentIcon}
          title="Service Charge"
          description="Per-store customer fees shown at checkout."
        >
          {storeList.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No stores available.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {storeList.map((store, index) => (
                <li
                  key={store.id || index}
                  className="flex items-start justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {store.name}
                    </p>
                    {store.customerfeeactive && (
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {store.customerfeeisrate ? (
                          <span>Fixed Rate: {store.customerfee}%</span>
                        ) : (
                          <div className="space-y-0.5">
                            <span className="block">
                              Fixed Price: ${store.customerfee}
                            </span>
                            {store.prices
                              ?.slice()
                              .sort((a, b) => a.startprice - b.startprice)
                              .map((price, i) => (
                                <span key={i} className="block pl-3">
                                  [${price.startprice} – ${price.endprice}] $
                                  {price.fee}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onClickOpenCustomerFeeUpsertModal(store)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-blue-600 transition hover:bg-blue-50 dark:text-sky-400 dark:hover:bg-sky-400/10"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      <span className="sr-only">Edit</span>
                    </button>
                    <Switch
                      checked={!!store.customerfeeactive}
                      disabled={loading.serviceCharge}
                      onChange={(e) =>
                        onChangeServiceCharge(e, 'customerfeeactive', index)
                      }
                      className={classNames(
                        store.customerfeeactive
                          ? 'bg-blue-600 dark:bg-sky-500'
                          : 'bg-slate-200 dark:bg-slate-700',
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:ring-sky-400 dark:focus:ring-offset-slate-800',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          store.customerfeeactive
                            ? 'translate-x-5'
                            : 'translate-x-0',
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        )}
                      />
                    </Switch>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Item Fee */}
        <SectionCard
          gradient="from-cyan-500 to-blue-600"
          icon={ShoppingBagIcon}
          title="Item Fee"
          description="Flat fee added to every item on an order."
        >
          <FieldLabel>Item Fee</FieldLabel>
          <div className="relative mt-2">
            <CurrencyInput
              id="input-price"
              name="itemfee"
              placeholder="0.00"
              className={inputClass}
              prefix="$"
              value={form.itemfee}
              decimalSeparator="."
              groupSeparator=","
              decimalScale={2}
              step={1}
              defaultValue={0}
              decimalsLimit={2}
              onValueChange={(value) => onChangeItemFee(value, 'itemfee')}
              onBlur={(e) => onBlurSaveItemFee(e.target.value, 'itemfee')}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Saved automatically when you leave the field.
          </p>
        </SectionCard>
      </div>

      {/* Credit Card Fee Modal */}
      <TWModal
        open={dialogs.creditCardUpsert}
        onClose={onCloseUpsertCreditCard}
        title="Edit Credit Card Fee"
        description="Update processing fees for this company."
        size="xl"
        showCloseButton
        dismissible={false}
        footer={
          <div className="flex items-center justify-end gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.creditCardLoading}
              onClick={onCloseUpsertCreditCard}
            />
            <TWButton
              variant="primary"
              label={loading.creditCardLoading ? 'Saving…' : 'Save Changes'}
              disabled={loading.creditCardLoading}
              loading={loading.creditCardLoading}
              onClick={() => onFormSubmit()}
            />
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel>Percentage</FieldLabel>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                %
              </span>
              <input
                value={form.creditcardfeerate}
                onChange={(e) =>
                  onFormChange(e.target.value, 'creditcardfeerate')
                }
                className={`${inputClass} pl-7`}
                placeholder="0.00"
                type="number"
              />
            </div>
          </div>
          <div>
            <FieldLabel>Per Transaction</FieldLabel>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                $
              </span>
              <input
                value={form.creditcardfeeper}
                onChange={(e) =>
                  onFormChange(e.target.value, 'creditcardfeeper')
                }
                className={`${inputClass} pl-7`}
                placeholder="0.00"
                type="number"
              />
            </div>
          </div>
        </div>
      </TWModal>

      {/* Customer Fee Modal */}
      <TWModal
        open={dialogs.customerFeeUpsert}
        onClose={onCloseUpsertCustomerFee}
        title="Customer Fee"
        description="Configure service fees for this store."
        size="2xl"
        showCloseButton
        dismissible={false}
        footer={
          <div className="flex items-center justify-end gap-2">
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.customerFeeLoading}
              onClick={onCloseUpsertCustomerFee}
            />
            <TWButton
              variant="primary"
              label={loading.customerFeeLoading ? 'Saving…' : 'Save Changes'}
              disabled={loading.customerFeeLoading}
              loading={loading.customerFeeLoading}
              onClick={onSubmitCustomerFee}
            />
          </div>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>Fee Type</FieldLabel>
              <Radio.Group
                className="mt-2"
                disabled={!selectedStore.customerfeeactive}
                onChange={(e) =>
                  setSelectedStore((prev) => ({
                    ...prev,
                    customerfeeisrate: e.target.value,
                  }))
                }
                value={selectedStore.customerfeeisrate}
              >
                <Radio value={true}>Percentage (%)</Radio>
                <Radio value={false}>Fixed ($)</Radio>
              </Radio.Group>
            </div>
            <div>
              <FieldLabel>
                <span className="inline-flex items-center gap-1">
                  <CurrencyDollarIcon className="h-3 w-3" />
                  {selectedStore.customerfeeisrate
                    ? 'Fixed Rate'
                    : 'Fixed Price'}
                </span>
              </FieldLabel>
              <div className="relative mt-2">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                  {selectedStore.customerfeeisrate ? '%' : '$'}
                </span>
                <input
                  disabled={!selectedStore.customerfeeactive}
                  value={selectedStore.customerfee || ''}
                  onChange={(e) =>
                    setSelectedStore((prev) => ({
                      ...prev,
                      customerfee: e.target.value,
                    }))
                  }
                  type="number"
                  className={`${inputClass} pl-7`}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {!selectedStore.customerfeeisrate && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <FieldLabel>Price Tiers</FieldLabel>
              <div className="mt-3 space-y-3">
                {selectedStore.prices?.map((price, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
                  >
                    <div>
                      <FieldLabel>Min Price</FieldLabel>
                      <div className="relative mt-2">
                        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                          $
                        </span>
                        <input
                          value={price.startprice}
                          onChange={(e) =>
                            onFormCustomerFeeChange(
                              e.target.value,
                              index,
                              'startprice',
                            )
                          }
                          className={`${inputClass} pl-7`}
                          placeholder="0.00"
                          type="number"
                        />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>Max Price</FieldLabel>
                      <div className="relative mt-2">
                        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                          $
                        </span>
                        <input
                          value={price.endprice}
                          onChange={(e) =>
                            onFormCustomerFeeChange(
                              e.target.value,
                              index,
                              'endprice',
                            )
                          }
                          className={`${inputClass} pl-7`}
                          placeholder="0.00"
                          type="number"
                        />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>Fee</FieldLabel>
                      <div className="relative mt-2">
                        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-400">
                          $
                        </span>
                        <input
                          value={price.fee}
                          onChange={(e) =>
                            onFormCustomerFeeChange(
                              e.target.value,
                              index,
                              'fee',
                            )
                          }
                          className={`${inputClass} pl-7`}
                          placeholder="0.00"
                          type="number"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onClickDeletePrice(index)}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"
                    >
                      <MinusCircleIcon className="h-5 w-5" />
                      <span className="sr-only">Remove tier</span>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  disabled={loading.customerFeeLoading}
                  onClick={onClickAddCustomerFee}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:border-blue-400 hover:text-blue-600 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-sky-400 dark:hover:text-sky-300"
                >
                  <PlusCircleIcon className="h-4 w-4" />
                  Add Fee Condition
                </button>
              </div>
            </div>
          )}
        </div>
      </TWModal>

      <ExtraFeesUpsertModal
        selectedExtraFee={selectedExtraFee}
        setSelectedExtraFee={setSelectedExtraFee}
        show={dialogs.extraFeesUpsert}
        loading={loading.extraFeesLoading}
        companyDetail={companyDetail}
        onCloseUpsert={onCloseUpsertExtraFee}
        updateCompany={updateCompany}
        onSubmit={onSubmitExtraFee}
        onClickDelete={onClickDeleteExtraFee}
      />
      <SubscriptionUpsertModal
        show={dialogs.subscriptionUpsert}
        companyDetail={companyDetail}
        errors={'errors'}
        onClickDeleteOptionGroup={'onClickDeleteOptionGroup'}
        onCloseUpsert={onCloseUpsert}
        updateCompany={updateCompany}
        onClickCreateOptionGroup={'onClickCreateOptionGroup'}
        onClickSaveOptionGroups={'onClickSaveOptionGroups'}
      />
    </div>
  );
};

export default Fees;
